import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaChartPie, FaSearchDollar, FaCogs, FaRegClipboard } from 'react-icons/fa';

const InvestmentManagementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', path: '/services' },
    { label: 'Investment Management' }
  ];

  const keyServices = [
    {
      icon: <FaChartPie className="w-8 h-8 text-gray-700" />,
      title: "Portfolio Construction",
      description: "Strategic asset allocation and portfolio design tailored to your specific investment objectives and risk tolerance."
    },
    {
      icon: <FaSearchDollar className="w-8 h-8 text-gray-700" />,
      title: "Investment Research",
      description: "Rigorous fundamental and quantitative research to identify the most promising investment opportunities across global markets."
    },
    {
      icon: <FaCogs className="w-8 h-8 text-gray-700" />,
      title: "Active Management",
      description: "Dynamic portfolio management with ongoing optimization and tactical adjustments to capitalize on changing market conditions."
    },
    {
      icon: <FaRegClipboard className="w-8 h-8 text-gray-700" />,
      title: "Performance Reporting",
      description: "Comprehensive performance tracking and transparent reporting to keep you informed about your investment progress."
    }
  ];

  const investmentPhilosophy = [
    {
      title: "Disciplined Approach",
      description: "We maintain a disciplined investment approach guided by rigorous research and proven methodologies rather than market emotions or short-term trends."
    },
    {
      title: "Risk Management",
      description: "Comprehensive risk assessment and management is integrated into every stage of our investment process to protect capital while pursuing growth."
    },
    {
      title: "Global Perspective",
      description: "Our investment approach leverages a global perspective to identify opportunities and manage risk across diverse markets and asset classes."
    },
    {
      title: "Long-Term Focus",
      description: "While remaining nimble to capture short-term opportunities, our core philosophy emphasizes long-term wealth creation and preservation."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Investment Management" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900">
                Sophisticated Investment Solutions
              </h2>
              <p className="text-lg text-gray-700">
                Blockfirst Capital's Investment Management service delivers sophisticated, tailored investment solutions designed to help you achieve your financial goals through all market conditions.
              </p>
              <p className="text-lg text-gray-700">
                Our experienced team combines deep market expertise, cutting-edge analytics, and disciplined investment processes to construct and manage portfolios that align with your specific objectives and risk profile.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Discuss Your Investment Needs
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/person-office-analyzing-checking-finance-graphs.jpg" 
                alt="Investment Analysis" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Investment Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a comprehensive suite of investment management services to address the diverse needs of individual and institutional investors.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {keyServices.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/co-workers-planning-strategy.jpg" 
                alt="Investment Strategy Planning" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary">
                Our Investment Philosophy
              </h3>
              <p className="text-lg text-gray-700">
                Our investment approach is guided by the following core principles:
              </p>
              
              <div className="space-y-4">
                {investmentPhilosophy.map((principle, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-gray-700">
                    <h4 className="text-xl font-heading font-semibold mb-2 text-primary">{principle.title}</h4>
                    <p className="text-gray-600">{principle.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2">$4.8B+</div>
                <div className="text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2">25+</div>
                <div className="text-gray-300">Years of Experience</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2">18.7%</div>
                <div className="text-gray-300">Average Annual Return</div>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Start Your Investment Journey
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default InvestmentManagementPage;
